export enum FrameFontFamily {
  Handwriting = 'InstaHelper Caveat',
  FilmStamp = 'InstaHelper DSEG7 Classic',
}

export class FontLoadError extends Error {
  constructor(message: string, options?: ErrorOptions) {
    super(`Polaroid frame fonts could not load: ${message}`, options)
    this.name = 'FontLoadError'
  }
}

interface FrameFontDefinition {
  family: FrameFontFamily
  url: string
  sample: string
}

interface WritableFontFaceSet {
  add(font: FontFace): void
  check(font: string, text?: string): boolean
}

const FRAME_FONT_DEFINITIONS = [
  {
    family: FrameFontFamily.Handwriting,
    url: '/fonts/Caveat-Variable.ttf',
    sample: 'Polaroid 2026',
  },
  {
    family: FrameFontFamily.FilmStamp,
    url: '/fonts/DSEG7Classic-Regular.woff2',
    sample: '0123456789',
  },
] as const satisfies readonly FrameFontDefinition[]

let frameFontsPromise: Promise<void> | null = null

function currentFontSet(): WritableFontFaceSet {
  const fontSet = typeof document === 'undefined'
    ? (globalThis as { fonts?: WritableFontFaceSet }).fonts
    : document.fonts
  if (!fontSet) throw new FontLoadError('this rendering context has no FontFaceSet')
  return fontSet
}

async function loadFrameFonts(): Promise<void> {
  if (typeof FontFace === 'undefined') throw new FontLoadError('this browser has no FontFace API')
  const fontSet = currentFontSet()
  for (const definition of FRAME_FONT_DEFINITIONS) {
    try {
      const face = await new FontFace(
        definition.family,
        `url("${definition.url}")`,
        { style: 'normal', weight: '400' },
      ).load()
      fontSet.add(face)
      if (!fontSet.check(`16px "${definition.family}"`, definition.sample)) {
        throw new Error(`loaded family "${definition.family}" is unavailable to canvas text`)
      }
    } catch (error) {
      if (error instanceof FontLoadError) throw error
      throw new FontLoadError(`failed to load "${definition.family}" from ${definition.url}`, {
        cause: error,
      })
    }
  }
}

export function ensureFrameFontsLoaded(): Promise<void> {
  frameFontsPromise ??= loadFrameFonts()
  return frameFontsPromise
}
